<!-- Единая модалка создания и редактирования точки маршрута для заказа и рейса -->
<template>
  <BaseModal
    :visible="true"
    :title="modalTitle"
    size="lg"
    :footer-align="isEditMode ? 'between' : 'end'"
    elevated-footer
    @close="$emit('close')"
  >
    <base-loader v-if="isLoading" />

    <form
      v-else
      :id="formId"
      class="route-point-form"
      @submit.prevent="savePoint"
    >
      <BaseFormSection title="Основное" columns="2" title-size="16px" hide-title>
        <BaseFormField
          label="Тип точки"
          for-id="point_type"
          required
          :filled="Boolean(point.point_type)"
          :error="errors.point_type"
          error-message="Это поле обязательно"
        >
          <base-select
            id="point_type"
            v-model="point.point_type"
            :options="tripStore.pointTypeOptions"
            placeholder="Выберите тип точки"
            :invalid="errors.point_type"
            @update:modelValue="errors.point_type = false"
          />
        </BaseFormField>

        <BaseFormField
          label="Вид погрузки"
          for-id="loading_method"
          :filled="Boolean(point.loading_method)"
        >
          <base-select
            id="loading_method"
            v-model="point.loading_method"
            :options="tripStore.loadingMethodOptions"
            placeholder="Выберите вид погрузки"
          />
        </BaseFormField>
      </BaseFormSection>

      <BaseFormSection title="Адрес" columns="2" title-size="16px" hide-title>
        <BaseFormField wide>
          <div class="map-wrapper">
            <div ref="mapContainer" class="map-container"></div>
            <div class="map-help" v-if="!point.lat">
              <small>Кликните по карте или введите адрес ниже</small>
            </div>
          </div>
        </BaseFormField>

        <BaseFormField
          label="Адрес"
          for-id="address"
          required
          :filled="Boolean(point.address)"
          info-key="routePoint.common"
          wide
          :error="errors.address"
          :error-message="errors.addressMessage"
        >
          <div class="address-input-wrapper">
            <base-address-autocomplete
              ref="addressAutocomplete"
              id="address"
              v-model="point.address"
              placeholder="Начните вводить адрес или выберите на карте..."
              :invalid="errors.address"
              @select="handleAddressSelect"
              @update:modelValue="handleAddressChange"
            />

            <div v-if="point.lat" class="coords-info" aria-live="polite">
              <span>
                Координаты:
                {{ Number(point.lat).toFixed(6) }},
                {{ Number(point.lon).toFixed(6) }}
              </span>
            </div>
          </div>
        </BaseFormField>

      </BaseFormSection>

      <RoutePointAddressDetailsSection
        :point="point"
        @update-field="updatePointField"
      />

      <BaseFormSection
        title="Время и контроль"
        columns="2"
        title-size="16px"
        hide-title
      >
        <BaseFormField
          label="Планируемые дата и время"
          for-id="planned_datetime"
          :filled="Boolean(point.planned_datetime)"
          info-key="routePoint.common"
        >
          <base-input
            id="planned_datetime"
            v-model="point.planned_datetime"
            type="datetime-local"
            :timezone="point.timezone"
          />
        </BaseFormField>

        <BaseFormField
          label="Часовой пояс"
          for-id="timezone"
          :filled="Boolean(point.timezone)"
          info-key="routePoint.common"
        >
          <TimezoneSelect
            id="timezone"
            v-model="point.timezone"
            placeholder="Определится после выбора адреса"
            disabled
          />
        </BaseFormField>

        <BaseFormField
          v-if="showActualDatetime"
          label="Фактические дата и время"
          for-id="actual_datetime"
          :filled="Boolean(point.actual_datetime)"
        >
          <base-input
            id="actual_datetime"
            v-model="point.actual_datetime"
            type="datetime-local"
            :timezone="point.timezone"
          />
        </BaseFormField>

        <BaseFormField
          label="Комментарий"
          for-id="comment"
          :filled="Boolean(point.comment)"
          info-key="routePoint.common"
          wide
        >
          <base-textarea id="comment" v-model="point.comment" />
        </BaseFormField>
      </BaseFormSection>
    </form>

    <template #footer>
      <base-button
        v-if="isEditMode"
        type="button"
        class="delete-btn"
        @click="deletePoint"
      >
        Удалить
      </base-button>

      <div class="footer-actions">
        <base-button type="button" @click="$emit('close')">
          Отмена
        </base-button>
        <base-primary-button type="submit" :form="formId">
          Сохранить
        </base-primary-button>
      </div>
    </template>
  </BaseModal>

  <BaseDialog
    :visible="showDeleteDialog"
    title="Удалить точку?"
    :message="deleteDialogMessage"
    :actions="[
      { key: 'confirm', label: 'Удалить', type: 'danger' },
      { key: 'close', label: 'Отмена', type: 'secondary' },
    ]"
    @action="handleDeleteDialogAction"
  />
</template>

<script>
import Feature from "ol/Feature";
import Map from "ol/Map";
import View from "ol/View";
import { defaults as defaultControls } from "ol/control";
import Point from "ol/geom/Point";
import TileLayer from "ol/layer/Tile";
import { Vector as VectorLayer } from "ol/layer";
import { fromLonLat, toLonLat } from "ol/proj";
import { Vector as VectorSource } from "ol/source";
import XYZ from "ol/source/XYZ";
import { Icon, Style } from "ol/style";
import "ol/ol.css";

import suggestionsApi from "@/services/api/suggestionsApi";
import orderPointsApi from "@/services/api/orderPointsApi";
import tripPointsApi from "@/services/api/tripPointsApi";
import { useTripStore } from "@/stores/tripStore";
import RoutePointAddressDetailsSection from "@/components/RoutePoint/AddressDetailsSection.vue";
import TimezoneSelect from "@/components/Telemetry/TimezoneSelect.vue";
import { parseAddress } from "@/utils/addressUtils";
import {
  buildPlannedDatetime,
  convertDatetimeBetweenTimezones,
  getTimezoneByCoords,
  toLocalDatetime,
} from "@/utils/timezoneUtils";

const DEFAULT_CENTER = [82.9346, 55.0415];

export default {
  name: "RoutePointModal",

  components: {
    RoutePointAddressDetailsSection,
    TimezoneSelect,
  },

  props: {
    resourceType: {
      type: String,
      required: true,
      validator: (value) => ["order", "trip"].includes(value),
    },
    resourceId: {
      type: [String, Number],
      required: true,
    },
    pointId: {
      type: [String, Number],
      default: null,
    },
    companyId: {
      type: Number,
      required: true,
    },
    existingPointsCount: {
      type: Number,
      default: 0,
    },
    showActualDatetime: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["close", "created", "updated", "deleted"],

  data() {
    return {
      point: this.blankPoint(),
      errors: {
        point_type: false,
        address: false,
        addressMessage: "",
      },
      isLoading: Boolean(this.pointId),
      isMapSelected: false,
      isLoadedFromServer: false,
      map: null,
      markerSource: null,
      showDeleteDialog: false,
    };
  },

  computed: {
    tripStore() {
      return useTripStore();
    },

    isEditMode() {
      return Boolean(this.pointId);
    },

    pointApi() {
      return this.resourceType === "order"
        ? orderPointsApi
        : tripPointsApi;
    },

    resourceLabel() {
      return this.resourceType === "order" ? "заказа" : "рейса";
    },

    modalTitle() {
      const action = this.isEditMode ? "Редактирование" : "Создание";
      return `${action} точки ${this.resourceLabel}`;
    },

    formId() {
      return `${this.resourceType}-point-form`;
    },

    deleteDialogMessage() {
      return `Точка будет удалена из ${this.resourceLabel}`;
    },

  },

  methods: {
    updatePointField(field, value) {
      this.point[field] = value;
    },

    blankPoint() {
      return {
        point_type: this.defaultPointType(),
        address: "",
        city: "",
        street: "",
        house: "",
        postal_code: "",
        region_code: "",
        lat: null,
        lon: null,
        loading_method: "",
        planned_datetime: "",
        actual_datetime: "",
        comment: "",
        timezone: "",
      };
    },

    defaultPointType() {
      if (this.existingPointsCount === 0) return "loading";
      if (this.existingPointsCount === 1) return "unloading";

      return "";
    },

    async loadPoint() {
      if (!this.isEditMode) return;

      try {
        const response = await this.pointApi.show(
          this.companyId,
          this.resourceId,
          this.pointId
        );
        this.point = { ...this.blankPoint(), ...response.data.result };
        this.applyTimezoneToDatetime();
        this.isLoadedFromServer = true;
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.initMap(this.mapCenter()));
      }
    },

    mapCenter() {
      if (this.point.lat && this.point.lon) {
        return [Number(this.point.lon), Number(this.point.lat)];
      }

      return DEFAULT_CENTER;
    },

    updateTimezone() {
      if (!this.point.lat || !this.point.lon) {
        this.point.timezone = "";
        return;
      }

      const newTimezone = getTimezoneByCoords(
        this.point.lat,
        this.point.lon
      );

      if (
        newTimezone &&
        this.point.timezone &&
        newTimezone !== this.point.timezone &&
        this.point.planned_datetime
      ) {
        this.point.planned_datetime = convertDatetimeBetweenTimezones(
          this.point.planned_datetime,
          this.point.timezone,
          newTimezone
        );
      }

      this.point.timezone = newTimezone;
    },

    applyTimezoneToDatetime() {
      if (!this.point.timezone && this.point.lat && this.point.lon) {
        this.point.timezone = getTimezoneByCoords(
          Number(this.point.lat),
          Number(this.point.lon)
        );
      }

      if (this.point.planned_datetime) {
        this.point.planned_datetime = toLocalDatetime(
          this.point.planned_datetime,
          this.point.timezone
        );
      }

      if (this.point.actual_datetime) {
        this.point.actual_datetime = toLocalDatetime(
          this.point.actual_datetime,
          this.point.timezone
        );
      }
    },

    initMap(centerLonLat) {
      if (this.map) return;

      const centerWebMercator = fromLonLat(centerLonLat);

      const tileLayer = new TileLayer({
        source: new XYZ({
          url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
          attributions:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }),
      });

      this.markerSource = new VectorSource();
      const markerLayer = new VectorLayer({
        source: this.markerSource,
        style: new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
            scale: 0.07,
            color: "#d32f2f",
          }),
        }),
      });

      this.map = new Map({
        target: this.$refs.mapContainer,
        layers: [tileLayer, markerLayer],
        view: new View({
          center: centerWebMercator,
          zoom: this.point.lat && this.point.lon ? 16 : 12,
          maxZoom: 19,
        }),
        controls: defaultControls({ attribution: false, zoom: true }),
      });

      this.map.on("click", this.handleMapClick);

      if (this.point.lat && this.point.lon) {
        this.updateMarker(centerWebMercator);
      }
    },

    handleAddressSelect(suggestion) {
      const data = suggestion.data || {};
      const address = parseAddress(data);

      this.point.lat = Number(data.geo_lat);
      this.point.lon = Number(data.geo_lon);
      this.point.address = suggestion.value;
      this.point.city = address.city;
      this.point.street = address.street;
      this.point.house = address.house;
      this.point.postal_code = address.index;
      this.point.region_code = address.regionCode;
      this.errors.address = false;
      this.errors.addressMessage = "";
      this.isMapSelected = false;
      this.isLoadedFromServer = false;

      this.updateTimezone();
      this.updateMapView([this.point.lon, this.point.lat], 18);
    },

    handleAddressChange(value) {
      this.errors.address = false;
      this.errors.addressMessage = "";
      this.isMapSelected = false;
      this.isLoadedFromServer = false;

      if (!value || value.trim().length === 0) {
        this.clearAddressDetails();
      }
    },

    clearAddressDetails() {
      this.point.lat = null;
      this.point.lon = null;
      this.point.city = "";
      this.point.street = "";
      this.point.house = "";
      this.point.postal_code = "";
      this.point.region_code = "";
      this.point.timezone = "";
      this.markerSource?.clear();
    },

    async handleMapClick(event) {
      const coords = event.coordinate;
      const lonLat = toLonLat(coords);

      this.point.lon = lonLat[0];
      this.point.lat = lonLat[1];
      this.errors.address = false;
      this.errors.addressMessage = "";
      this.isMapSelected = true;
      this.isLoadedFromServer = false;

      this.updateTimezone();
      this.updateMarker(coords);

      try {
        const response = await suggestionsApi.geolocate(
          this.point.lat,
          this.point.lon
        );
        const suggestions =
          response.data?.body?.suggestions ||
          response.data?.suggestions;

        if (suggestions && suggestions.length > 0) {
          this.applySuggestion(suggestions[0]);
        } else {
          this.applyFallbackAddress();
        }
      } catch (e) {
        console.error("Не удалось определить адрес", e);
        this.applyFallbackAddress();
      }
    },

    applySuggestion(suggestion) {
      const address = parseAddress(suggestion.data || {});

      this.point.address = suggestion.value;
      this.point.city = address.city;
      this.point.street = address.street;
      this.point.house = address.house;
      this.point.postal_code = address.index;
      this.point.region_code = address.regionCode;
    },

    applyFallbackAddress() {
      this.point.address = `${this.point.lat.toFixed(
        5
      )}, ${this.point.lon.toFixed(5)}`;
      this.point.city = "";
      this.point.street = "";
      this.point.house = "";
      this.point.postal_code = "";
      this.point.region_code = "";
    },

    updateMapView(lonLat, zoomLevel = 16) {
      if (!this.map) return;

      const coords = fromLonLat(lonLat);
      this.map
        .getView()
        .animate({ center: coords, zoom: zoomLevel, duration: 500 });
      this.updateMarker(coords);
    },

    updateMarker(coords) {
      this.markerSource.clear();
      const feature = new Feature({ geometry: new Point(coords) });
      this.markerSource.addFeature(feature);
    },

    validateForm() {
      let isValid = true;
      this.errors = {
        point_type: false,
        address: false,
        addressMessage: "",
      };

      if (!this.point.point_type) {
        this.errors.point_type = true;
        isValid = false;
      }

      if (!this.point.address || !this.point.lat || !this.point.lon) {
        this.errors.address = true;
        this.errors.addressMessage =
          "Выберите адрес из списка или укажите на карте";
        return false;
      }

      if (this.isLoadedFromServer || this.isMapSelected) {
        return isValid;
      }

      const autocomplete = this.$refs.addressAutocomplete;

      if (!autocomplete.isAddressSelected()) {
        if (autocomplete.hasAvailableSuggestions()) {
          const firstSuggestion = autocomplete.getFirstSuggestion();
          this.handleAddressSelect(firstSuggestion);
          autocomplete.forceUpdateValue(firstSuggestion.value);
          return true;
        }

        this.errors.address = true;
        this.errors.addressMessage =
          "Выберите адрес из выпадающего списка";
        this.$nextTick(() => autocomplete.focusInput());
        return false;
      }

      return isValid;
    },

    async savePoint() {
      if (!this.validateForm()) return;

      try {
        if (this.isEditMode) {
          await this.updatePoint();
          this.$emit("updated");
        } else {
          await this.createPoint();
          this.$emit("created");
        }

        this.$emit("close");
      } catch (e) {
        console.error("Ошибка сохранения точки маршрута:", e);
      }
    },

    createPoint() {
      return this.pointApi.create(
        this.companyId,
        this.resourceId,
        this.payload()
      );
    },

    updatePoint() {
      return this.pointApi.update(
        this.companyId,
        this.resourceId,
        this.pointId,
        this.payload()
      );
    },

    deletePoint() {
      this.showDeleteDialog = true;
    },

    async handleDeleteDialogAction(action) {
      this.showDeleteDialog = false;
      if (action !== "confirm") return;

      try {
        await this.pointApi.delete(
          this.companyId,
          this.resourceId,
          this.pointId
        );
        this.$emit("deleted");
        this.$emit("close");
      } catch (e) {
        console.error("Ошибка удаления точки маршрута:", e);
      }
    },

    payload() {
      const payload = {
        point_type: this.point.point_type,
        address: this.point.address,
        city: this.point.city,
        street: this.point.street,
        house: this.point.house,
        postal_code: this.point.postal_code,
        region_code: this.point.region_code,
        lat: String(this.point.lat),
        lon: String(this.point.lon),
        loading_method: this.point.loading_method,
        planned_datetime: this.datetimePayload(
          this.point.planned_datetime
        ),
        comment: this.point.comment,
        timezone: this.point.timezone,
      };

      if (this.showActualDatetime) {
        payload.actual_datetime = this.datetimePayload(
          this.point.actual_datetime
        );
      }

      return payload;
    },

    datetimePayload(value) {
      return buildPlannedDatetime(value, this.point.timezone);
    },
  },

  mounted() {
    if (this.isEditMode) {
      this.loadPoint();
    } else {
      this.$nextTick(() => this.initMap(DEFAULT_CENTER));
    }
  },

  beforeUnmount() {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
  },
};
</script>

<style scoped>
.route-point-form {
  display: grid;
  gap: 0;
}

.map-wrapper {
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.map-container {
  height: 300px;
  width: 100%;
  background-color: #f0f0f0;
  cursor: crosshair;
}

.map-help {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 5px 10px;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  z-index: 10;
}

.address-input-wrapper {
  position: relative;
}

.coords-info {
  position: absolute;
  right: 0;
  top: calc(100% + 3px);
  z-index: 1;
  pointer-events: none;
}

.coords-info span {
  display: inline-flex;
  max-width: 100%;
  padding: 0;
  color: #637381;
  font-size: 12px;
  line-height: 14px;
}

.delete-btn {
  background-color: #fff;
  border: 1px solid var(--color-danger);
  color: var(--color-danger);
}

.delete-btn:hover {
  background-color: var(--color-danger);
  color: #fff;
}

.footer-actions {
  display: flex;
  gap: 15px;
}

@media screen and (max-width: 767px) {
  .footer-actions {
    flex-direction: column-reverse;
    width: 100%;
  }

  .delete-btn {
    width: 100%;
  }
}
</style>
